import React, { useRef, useEffect, useState } from 'react';
import { Rect, Circle, Text, Line } from 'react-konva';
import { useCanvas } from '../../hooks/useCanvas';
import { useAuth } from '../../hooks/useAuth';
import { lockShape, unlockShape } from '../../services/canvas';
import { CANVAS_BOUNDS, DEFAULT_FILL } from '../../utils/constants';

function Shape({
  id,
  type = 'rectangle',
  x = 100,
  y = 100,
  width = 100,
  height = 100,
  radius = 50,
  text = 'Text',
  fontSize = 18,
  fontFamily = 'Arial',
  points = [0, 0, 100, 0],
  stroke = '#000000',
  strokeWidth = 2,
  fill = DEFAULT_FILL,
  isSelected = false,
  isLocked = false,
  lockedBy = null,
  groupId = null,
  isRecentAI = false,
  isEditing = false
}) {
  console.log('📐 Rendering shape:', { id: id.slice(0, 8), type, x, y });
  const shapeRef = useRef();
  const timeoutRef = useRef();
  const dragStartPosRef = useRef(null);
  const { currentUser } = useAuth();
  const { selectShapeWithGroup, updateShape, getShapesInGroup, shapes } = useCanvas();
  
  // Drag performance optimization - local state during drag
  const [dragPosition, setDragPosition] = useState({ x, y });
  const [isDragging, setIsDragging] = useState(false);

  // Handle shape click - select the shape (or entire group if grouped)
  const handleClick = (e) => {
    e.cancelBubble = true; // Prevent event from bubbling to stage
    
    if (isLocked && lockedBy && lockedBy !== currentUser?.uid) {
      console.log(`Shape is locked by ${lockedBy}`);
      return;
    }
    
    // Use selectShapeWithGroup to select entire group if shape is grouped
    selectShapeWithGroup(id);
  };

  // Handle drag start - only allow if shape is selected
  const handleDragStart = async (e) => {
    // Prevent drag if shape is not selected
    if (!isSelected) {
      e.target.stopDrag();
      console.log('Cannot drag: Shape not selected. Click to select first.');
      return;
    }

    // Prevent drag if locked by another user
    const isLockedByOther = isLocked && lockedBy && lockedBy !== currentUser?.uid;
    if (isLockedByOther) {
      e.target.stopDrag();
      console.log('Cannot drag: Shape is locked by another user');
      return;
    }

    if (!currentUser) {
      console.warn('Cannot lock shape: No authenticated user');
      e.target.stopDrag();
      return;
    }

    // Set dragging state for local updates
    setIsDragging(true);

    try {
      // Store initial position for group movement
      const currentPos = e.target.position();
      dragStartPosRef.current = { x: currentPos.x, y: currentPos.y };
      setDragPosition({ x: currentPos.x, y: currentPos.y });
      
      // Lock the shape for this user
      await lockShape(id, currentUser.uid);
      
      // If shape is in a group, lock all shapes in the group
      if (groupId) {
        const groupShapes = getShapesInGroup(groupId);
        console.log(`📦 Dragging group with ${groupShapes.length} shape(s)`);
        for (const shape of groupShapes) {
          if (shape.id !== id) {
            await lockShape(shape.id, currentUser.uid);
          }
        }
      }
      
      // Set auto-unlock timeout (5 seconds)
      timeoutRef.current = setTimeout(async () => {
        console.log('Auto-unlocking shape due to timeout:', id);
        try {
          await unlockShape(id);
          if (groupId) {
            const groupShapes = getShapesInGroup(groupId);
            for (const shape of groupShapes) {
              if (shape.id !== id) {
                await unlockShape(shape.id);
              }
            }
          }
        } catch (error) {
          console.error('Error auto-unlocking shape:', error);
        }
      }, 5000);
      
      console.log('Shape locked for drag:', id);
    } catch (error) {
      console.error('Error locking shape:', error);
      // If locking fails, prevent drag
      setIsDragging(false);
      e.target.stopDrag();
      return false;
    }
  };

  // Handle drag move - update local position only (no Firestore)
  const handleDragMove = (e) => {
    if (isDragging) {
      const node = e.target;
      // Update local position only - no Firestore write
      setDragPosition({ 
        x: node.x(), 
        y: node.y() 
      });
    }
  };

  // Handle drag end - update position and unlock (ONLY Firestore write happens here)
  const handleDragEnd = async (e) => {
    if (!isDragging) return;
    
    const newPos = e.target.position();
    
    try {
      // Clear auto-unlock timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Calculate delta from drag start
      const deltaX = newPos.x - (dragStartPosRef.current?.x || newPos.x);
      const deltaY = newPos.y - (dragStartPosRef.current?.y || newPos.y);
      
      // Update final position in local state
      setDragPosition({ x: newPos.x, y: newPos.y });
      
      // NOW update Firestore (only once, at end of drag)
      await updateShape(id, {
        x: newPos.x,
        y: newPos.y
      });
      
      // If shape is in a group, move all other shapes in the group by the same delta
      if (groupId && dragStartPosRef.current) {
        const groupShapes = getShapesInGroup(groupId);
        console.log(`📦 Moving group: Δx=${deltaX.toFixed(1)}, Δy=${deltaY.toFixed(1)}`);
        
        for (const shape of groupShapes) {
          if (shape.id !== id) {
            await updateShape(shape.id, {
              x: shape.x + deltaX,
              y: shape.y + deltaY
            });
          }
        }
      }
      
      // Unlock the shape and group shapes
      if (currentUser) {
        await unlockShape(id);
        
        if (groupId) {
          const groupShapes = getShapesInGroup(groupId);
          for (const shape of groupShapes) {
            if (shape.id !== id) {
              await unlockShape(shape.id);
            }
          }
        }
        
        console.log('Shape unlocked after drag:', id);
      }
      
      // Reset drag start position
      dragStartPosRef.current = null;
    } catch (error) {
      console.error('Error in drag end:', error);
    } finally {
      // Always clear dragging state
      setIsDragging(false);
    }
  };

  // Drag bound function to keep shape within canvas boundaries
  const dragBoundFunc = (pos) => {
    const newPos = { ...pos };
    
    // Constrain to canvas boundaries
    newPos.x = Math.max(CANVAS_BOUNDS.minX, Math.min(pos.x, CANVAS_BOUNDS.maxX - width));
    newPos.y = Math.max(CANVAS_BOUNDS.minY, Math.min(pos.y, CANVAS_BOUNDS.maxY - height));
    
    return newPos;
  };

  // Handle mouse enter - show hover effect
  const handleMouseEnter = (e) => {
    const stage = e.target.getStage();
    if (stage) {
      const isLockedByOther = isLocked && lockedBy && lockedBy !== currentUser?.uid;
      // Show move cursor only if selected, otherwise pointer cursor
      if (isLockedByOther) {
        stage.container().style.cursor = 'not-allowed';
      } else if (isSelected) {
        stage.container().style.cursor = 'move';
      } else {
        stage.container().style.cursor = 'pointer';
      }
    }
  };

  // Handle mouse leave - reset cursor
  const handleMouseLeave = (e) => {
    const stage = e.target.getStage();
    if (stage) {
      stage.container().style.cursor = 'default';
    }
  };

  // Handle double-click - trigger edit mode for text
  const handleDoubleClick = () => {
    if (type === 'text' && isSelected && !isEditing) {
      updateShape(id, { isEditing: true });
    }
  };

  // Determine visual properties based on state
  const getVisualProps = () => {
    const isLockedByOther = isLocked && lockedBy && lockedBy !== currentUser?.uid;
    const isLockedByMe = isLocked && lockedBy === currentUser?.uid;
    
    if (isLockedByOther) {
      // Locked by another user - red border, non-draggable
      return {
        stroke: '#ef4444', // Red
        strokeWidth: 2,
        opacity: 0.7,
        dash: [5, 5], // Dashed border for locked state
        draggable: false
      };
    }
    
    if (isLockedByMe) {
      // Locked by current user (during drag) - orange border
      return {
        stroke: '#f59e0b', // Orange
        strokeWidth: 2,
        opacity: 0.9,
        dash: [3, 3], // Different dash pattern
        draggable: isSelected // Only draggable if selected
      };
    }
    
    if (isSelected) {
      // Selected - blue border, draggable
      return {
        stroke: '#3b82f6', // Blue
        strokeWidth: 2,
        opacity: 1,
        draggable: true
      };
    }
    
    // Default state - no border, NOT draggable until selected
    return {
      stroke: 'transparent',
      strokeWidth: 0,
      opacity: 1,
      draggable: false // Must click to select before dragging
    };
  };


  // Sync drag position with prop changes (when not dragging)
  useEffect(() => {
    if (!isDragging) {
      setDragPosition({ x, y });
    }
  }, [x, y, isDragging]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const visualProps = getVisualProps();
  
  // Use local drag position during drag, otherwise use prop position
  const displayPosition = isDragging ? dragPosition : { x, y };
  
  // Don't render text shapes that are in editing mode (EditableText component handles them)
  if (type === 'text' && isEditing) {
    return null;
  }

  // Add AI sparkle effect
  const aiSparkleProps = isRecentAI ? {
    shadowColor: '#fbbf24', // Golden/amber color
    shadowBlur: 20,
    shadowOffset: { x: 0, y: 0 },
    shadowOpacity: 0.8,
    shadowEnabled: true
  } : {};

  // Common props for all shape types
  const commonProps = {
    ref: shapeRef,
    id: id,
    x: displayPosition.x, // Use local position during drag
    y: displayPosition.y, // Use local position during drag
    draggable: visualProps.draggable,
    dragDistance: 5, // Require 5px movement before drag starts (prevents accidental drag on click)
    dragBoundFunc: dragBoundFunc,
    onClick: handleClick,
    onTap: handleClick,
    onDblClick: handleDoubleClick,
    onDblTap: handleDoubleClick,
    onDragStart: handleDragStart,
    onDragMove: handleDragMove, // Update local position during drag
    onDragEnd: handleDragEnd,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    perfectDrawEnabled: false,
    shadowForStrokeEnabled: false,
    ...aiSparkleProps, // Add AI sparkle effect
    onMouseOver: (e) => {
      const canHover = !isLocked || lockedBy === currentUser?.uid;
      if (canHover) {
        e.target.opacity(0.8);
      }
    },
    onMouseOut: (e) => {
      e.target.opacity(visualProps.opacity);
    }
  };

  // Render different components based on shape type
  const renderShape = () => {
    switch (type) {
      case 'rectangle':
        return (
          <Rect
            {...commonProps}
            width={width}
            height={height}
            fill={fill}
            stroke={visualProps.stroke}
            strokeWidth={visualProps.strokeWidth}
            dash={visualProps.dash}
            opacity={visualProps.opacity}
          />
        );

      case 'circle':
        return (
          <Circle
            {...commonProps}
            radius={radius}
            fill={fill}
            stroke={visualProps.stroke}
            strokeWidth={visualProps.strokeWidth}
            dash={visualProps.dash}
            opacity={visualProps.opacity}
          />
        );

      case 'text':
        return (
          <Text
            {...commonProps}
            text={text || 'Double-click to edit'}
            fontSize={fontSize}
            fontFamily={fontFamily}
            fill={text ? fill : '#999999'}
            stroke={visualProps.stroke}
            strokeWidth={visualProps.strokeWidth}
            opacity={visualProps.opacity}
            fontStyle={!text ? 'italic' : 'normal'}
          />
        );

      case 'line':
        return (
          <Line
            {...commonProps}
            points={points}
            stroke={stroke}
            strokeWidth={strokeWidth}
            opacity={visualProps.opacity}
          />
        );

      default:
        console.warn('Unknown shape type:', type, '- rendering as rectangle');
        return (
          <Rect
            {...commonProps}
            width={width}
            height={height}
            fill={fill}
            stroke={visualProps.stroke}
            strokeWidth={visualProps.strokeWidth}
            dash={visualProps.dash}
            opacity={visualProps.opacity}
          />
        );
    }
  };

  return renderShape();
}

export default Shape;
